<!--
  Gabarit de largeur : invisible et hors flux (hauteur nulle), mais pris en compte
  dans la largeur intrinsèque du composant. Le champ ne peut donc plus être plus
  étroit que son option la plus longue.
-->
<div class="sl-sizer" [class]="'sl-sizer--' + size" aria-hidden="true">
  @if (placeholder) {
    <span class="sl-sizer__item">{{ placeholder | translate }}</span>
  }
  @for (opt of options(); track opt.id) {
    <span class="sl-sizer__item">{{ opt.label }}</span>
  }
</div>

<div
  class="sl-trigger"
  [class]="'sl-trigger--' + size"
  [class.sl-trigger--focused]="isFocused()"
  [class.sl-trigger--open]="isOpen()"
  [class.sl-trigger--disabled]="disabled"
  [class.sl-trigger--placeholder]="!selectedOption()"
  tabindex="0"
  role="combobox"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-controls]="uid + '-listbox'"
  [attr.aria-activedescendant]="activeDescendant()"
  (click)="toggle()"
  (focus)="onFocus()"
  (blur)="onBlur()"
>
  @if (addIconLeft) {
    <span class="sl-trigger__icon">
      <ng-content select="[slot='icon-left']"></ng-content>
    </span>
  }

  @if (isSearchable()) {
    <input
      #selectInput
      class="sl-trigger__input"
      type="text"
      autocomplete="off"
      [attr.aria-activedescendant]="activeDescendant()"
      [class.sl-trigger__input--selection]="showSelectionAsPlaceholder()"
      [attr.placeholder]="showSelectionAsPlaceholder() ? displayLabel() : (placeholder | translate)"
      [disabled]="disabled"
      [ngModel]="inputTerm()"
      (ngModelChange)="onSearchTerm($event)"
      data-attr="select-search-input">
  } @else {
    <span class="sl-trigger__label">{{ displayLabel() || (placeholder | translate) }}</span>
  }

  @if (multiple() && selectedCount() > 0) {
    <span class="sl-trigger__count">{{ selectedCount() }}</span>
  }

  @if (clearable && hasValue() && !disabled) {
    <button
      type="button"
      class="sl-trigger__clear"
      aria-label="Effacer"
      (mousedown)="$event.preventDefault()"
      (click)="clear($event)"
      data-attr="select-clear-button">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.27337 14.7266C1.63786 15.0911 2.22881 15.0911 2.5933 14.7266L8 9.31993L13.4067 14.7266C13.7712 15.0911 14.3621 15.0911 14.7266 14.7266C15.0911 14.3621 15.0911 13.7712 14.7266 13.4067L9.31993 8L14.7266 2.5933C15.0911 2.22881 15.0911 1.63786 14.7266 1.27337C14.3621 0.908878 13.7712 0.908878 13.4067 1.27337L8 6.68007L2.5933 1.27337C2.22881 0.908878 1.63786 0.908878 1.27337 1.27337C0.908878 1.63786 0.908878 2.22881 1.27337 2.5933L6.68007 8L1.27337 13.4067C0.908878 13.7712 0.908878 14.3621 1.27337 14.7266Z" fill="currentColor"/>
      </svg>
    </button>
  }

  <span class="sl-trigger__chevron-wrap">
    @if (addIconRight) {
      <ng-content select="[slot='icon-right']"></ng-content>
    } @else {
      <svg class="sl-trigger__chevron"
           xmlns="http://www.w3.org/2000/svg"
           width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M0.35 12.62C0.58 12.86 0.88 13 1.2 13C1.52 13 1.82 12.86 2.05 12.62L8 6.15L13.95 12.62C14.18 12.86 14.48 12.99 14.8 12.99C15.12 12.99 15.42 12.85 15.64 12.61C15.86 12.37 15.99 12.04 15.99 11.7C15.99 11.36 15.87 11.03 15.65 10.78L8.86 3.38C8.63 3.14 8.33 3 8.01 3C7.69 3 7.39 3.14 7.16 3.38L0.35 10.77C0.12 11.01 0 11.35 0 11.69C0 12.03 0.13 12.37 0.35 12.61V12.62Z" fill="currentColor"/>
      </svg>
    }
  </span>
</div>

@if (isOpen()) {
  <div class="sl-dropdown" [ngStyle]="dropdownStyle()" role="listbox" [id]="uid + '-listbox'">
    @if (!visibleOptions().length) {
      <div class="sl-dropdown__empty">{{ 'GLOBAL.NO_ITEM_FOUND' | translate }}</div>
    }
    @for (opt of visibleOptions(); track opt.id; let i = $index) {
      @if (multiple() && withCheckboxes) {
        <div class="sl-dropdown__option sl-dropdown__option--checkbox"
             [class.sl-dropdown__option--selected]="isSelectedInMulti(opt)"
             [class.sl-dropdown__option--disabled]="opt.disabled"
             [class.sl-dropdown__option--active]="activeIndex() === i"
             [id]="uid + '-opt-' + i"
             [attr.aria-selected]="isSelectedInMulti(opt)"
             (mouseenter)="activeIndex.set(i)"
             role="option">
          <app-checkbox
            color="mint"
            size="medium"
            [label]="opt.label"
            [state]="opt.disabled ? 'disabled' : 'default'"
            [ngModel]="isSelectedInMulti(opt)"
            (ngModelChange)="toggleMultipleOption(opt, $event)"
            [fullWidth]="true">
          </app-checkbox>
        </div>
      } @else {
        <div class="sl-dropdown__option"
             [class.sl-dropdown__option--selected]="isSelected(opt)"
             [class.sl-dropdown__option--disabled]="opt.disabled"
             [class.sl-dropdown__option--active]="activeIndex() === i"
             [id]="uid + '-opt-' + i"
             [attr.aria-selected]="isSelected(opt)"
             (mouseenter)="activeIndex.set(i)"
             role="option"
             [title]="opt.label"
             (click)="selectOption(opt)">
          <span class="sl-dropdown__option-label">{{ opt.label }}</span>
        </div>
      }
    }

    @if (footerLabel) {
      <button type="button" class="sl-dropdown__footer" (click)="clickFooter()">
        <span class="sl-dropdown__footer-label">{{ footerLabel }}</span>
      </button>
    }
  </div>
}